import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { TID } from "@/lib/testIds";
import { formatDate, statusClass, priorityStyle } from "@/lib/format";
import { Radio, Gavel } from "lucide-react";

export default function Governance() {
  const [loops, setLoops] = useState([]);
  const [decisions, setDecisions] = useState([]);
  const [selected, setSelected] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    Promise.all([
      api.get("/governance/loops"),
      api.get("/governance/decisions"),
    ])
      .then(([l, d]) => {
        setLoops(l.data.items);
        setDecisions(d.data.items);
      })
      .catch((e) => setErr(e?.response?.data?.detail || e.message));
  }, []);

  return (
    <div data-testid={TID.governancePage} className="space-y-4">
      <p className="text-sm text-neutral-400 max-w-2xl">
        Loop aperti (#33..#40) e decisioni (DEC A..G). Clicca per aprire il pannello dettaglio.
      </p>

      {err && (
        <div className="border border-red-900/60 bg-red-950/20 px-4 py-3 font-mono text-xs text-red-300">
          {err}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_1fr_360px] gap-4">
        <Section title="Loop aperti" icon={Radio} count={loops.length}>
          <ul className="divide-y divide-[#1F1F1F]">
            {loops.map((l) => (
              <li
                key={l.id}
                data-testid={TID.loopItem}
                onClick={() => setSelected({ kind: "loop", item: l })}
                className={`px-4 py-3 flex items-start gap-3 cursor-pointer hover:bg-[#141414] ${
                  selected?.item?.id === l.id ? "bg-[#141414]" : ""
                }`}
              >
                <div className="w-14 shrink-0">
                  <div className="font-mono text-[11px] text-neutral-300">{l.id}</div>
                  <span className={`font-mono text-[9px] uppercase tracking-widest border px-1 py-[1px] inline-block mt-0.5 ${priorityStyle(l.priority)}`}>
                    {l.priority}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-[#EDEDED] truncate">{l.title}</div>
                  <div className="font-mono text-[10px] text-neutral-500 mt-1">
                    {formatDate(l.opened_at)} · owner: {l.owner}
                  </div>
                </div>
                <span className={`font-mono text-[9px] uppercase tracking-widest border px-1.5 py-0.5 shrink-0 ${statusClass(l.status)}`}>
                  {l.status}
                </span>
              </li>
            ))}
          </ul>
        </Section>

        <Section title="Decisioni" icon={Gavel} count={decisions.length}>
          <ul className="divide-y divide-[#1F1F1F]">
            {decisions.map((d) => (
              <li
                key={d.id}
                data-testid={TID.decisionItem}
                onClick={() => setSelected({ kind: "decision", item: d })}
                className={`px-4 py-3 flex items-start gap-3 cursor-pointer hover:bg-[#141414] ${
                  selected?.item?.id === d.id ? "bg-[#141414]" : ""
                }`}
              >
                <div className="w-14 shrink-0 font-mono text-[11px] text-neutral-300">{d.id}</div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-[#EDEDED]">{d.title}</div>
                  <div className="font-mono text-[10px] text-neutral-500 mt-1">
                    {formatDate(d.decided_at)} · {d.decided_by}
                  </div>
                </div>
                <span className={`font-mono text-[9px] uppercase tracking-widest border px-1.5 py-0.5 shrink-0 ${statusClass(d.outcome)}`}>
                  {d.outcome}
                </span>
              </li>
            ))}
          </ul>
        </Section>

        <DetailPanel selected={selected} onClose={() => setSelected(null)} />
      </div>
    </div>
  );
}

function Section({ title, icon: Icon, count, children }) {
  return (
    <section className="border border-[#1F1F1F] bg-[#0C0C0C]">
      <header className="border-b border-[#1F1F1F] px-4 py-3 flex items-center justify-between bg-[#141414]">
        <div className="flex items-center gap-2">
          <Icon className="w-3.5 h-3.5 text-neutral-500" strokeWidth={1.5} />
          <span className="font-mono text-[10px] tracking-[0.24em] uppercase text-neutral-400">
            {title}
          </span>
        </div>
        <span className="font-mono text-[10px] text-neutral-600">{count}</span>
      </header>
      {children}
    </section>
  );
}

function DetailPanel({ selected, onClose }) {
  return (
    <section className="border border-[#1F1F1F] bg-[#0C0C0C] h-fit sticky top-24">
      <header className="border-b border-[#1F1F1F] px-4 py-3 flex items-center justify-between bg-[#141414]">
        <span className="font-mono text-[10px] tracking-[0.24em] uppercase text-neutral-400">
          Dettaglio
        </span>
        {selected && (
          <button
            onClick={onClose}
            className="font-mono text-[10px] text-neutral-500 hover:text-[#EDEDED]"
          >
            close
          </button>
        )}
      </header>
      {!selected && (
        <div className="p-4 font-mono text-xs text-neutral-500">
          Clicca un loop o una decisione per vederne il dettaglio.
        </div>
      )}
      {selected?.kind === "loop" && <LoopDetail item={selected.item} />}
      {selected?.kind === "decision" && <DecisionDetail item={selected.item} />}
    </section>
  );
}

function LoopDetail({ item }) {
  return (
    <div className="p-4 space-y-3">
      <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-neutral-500">
        loop · {item.id} · <span className={priorityStyle(item.priority).split(" ")[0]}>{item.priority}</span>
      </div>
      <h3 className="font-heading uppercase text-lg tracking-tight">{item.title}</h3>
      <div className={`inline-block font-mono text-[9px] uppercase tracking-widest border px-1.5 py-0.5 ${statusClass(item.status)}`}>
        {item.status}
      </div>
      <div className="pt-2 border-t border-[#1F1F1F] font-mono text-xs text-neutral-500 space-y-1">
        <div>owner · <span className="text-neutral-300">{item.owner}</span></div>
        <div>aperto · <span className="text-neutral-300">{formatDate(item.opened_at, { withTime: true })}</span></div>
      </div>
      {item.context && (
        <div className="pt-2 border-t border-[#1F1F1F]">
          <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-neutral-500 mb-1">contesto</div>
          <p className="text-sm text-neutral-300">{item.context}</p>
        </div>
      )}
    </div>
  );
}

function DecisionDetail({ item }) {
  return (
    <div className="p-4 space-y-3">
      <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-neutral-500">
        decisione · {item.id}
      </div>
      <h3 className="font-heading uppercase text-lg tracking-tight">{item.title}</h3>
      <div className={`inline-block font-mono text-[9px] uppercase tracking-widest border px-1.5 py-0.5 ${statusClass(item.outcome)}`}>
        {item.outcome}
      </div>
      <div className="pt-2 border-t border-[#1F1F1F] font-mono text-xs text-neutral-500 space-y-1">
        <div>decisa da · <span className="text-neutral-300">{item.decided_by}</span></div>
        <div>data · <span className="text-neutral-300">{formatDate(item.decided_at, { withTime: true })}</span></div>
      </div>
      {item.rationale && (
        <div className="pt-2 border-t border-[#1F1F1F]">
          <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-neutral-500 mb-1">motivazione</div>
          <p className="text-sm text-neutral-300">{item.rationale}</p>
        </div>
      )}
    </div>
  );
}
